import React, { useState } from "react";
import { apiClient } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { AlertTriangle, TrendingDown, Clock, Loader2, Sparkles, CheckCircle2 } from "lucide-react";

const RISK_STYLES = {
  high:   { pill: "bg-red-500 text-white",       badge: "bg-red-500/10 border-red-500/40 text-red-500",     icon: AlertTriangle },
  medium: { pill: "bg-amber-500 text-white",     badge: "bg-amber-500/10 border-amber-500/40 text-amber-500", icon: TrendingDown },
  low:    { pill: "bg-emerald-500 text-white",   badge: "bg-emerald-500/10 border-emerald-500/40 text-emerald-500", icon: CheckCircle2 },
};

export default function DelayPrediction() {
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);

  const runPrediction = async () => {
    setBusy(true);
    try {
      const { data: res } = await apiClient().post("/ai/delay-prediction");
      setData(res);
      toast.success(`Analysis complete — ${res.summary.high_risk} high-risk projects`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Prediction failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5 fade-in-up" data-testid="delay-prediction-page">
      <div>
        <div className="mono text-[10px] uppercase tracking-[0.25em] text-primary flex items-center gap-1.5"><Sparkles className="h-3 w-3" /> AI Feature</div>
        <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl mt-1 tracking-tight">Delay Prediction Engine</h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
          AI analyses your projects, attendance trends, incidents, and material stock to flag delay risks — before they hit the timeline.
        </p>
      </div>

      <button onClick={runPrediction} disabled={busy} data-testid="run-prediction-btn"
        className="btn-sharp inline-flex items-center gap-2 bg-primary text-primary-foreground px-5 py-2.5 text-sm font-medium disabled:opacity-50">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
        {busy ? "Analysing…" : (data ? "Re-run prediction" : "Predict delays now")}
      </button>

      {data && (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="prediction-summary">
            <SummaryCard label="Total projects" val={data.summary.total_projects} tone="muted" />
            <SummaryCard label="High risk" val={data.summary.high_risk} tone="red" />
            <SummaryCard label="Medium risk" val={data.summary.medium_risk} tone="amber" />
            <SummaryCard label="Low / on-track" val={data.summary.low_risk} tone="emerald" />
          </div>

          {data.risks.length === 0 && (
            <div className="border border-border bg-card p-6 text-center text-sm text-muted-foreground">
              {data.message || "AI could not compute risks from available data. Add project timelines, attendance and incidents to enable predictions."}
            </div>
          )}

          {/* Risk cards */}
          {data.risks.length > 0 && (
            <div className="grid md:grid-cols-2 gap-4" data-testid="risk-cards">
              {data.risks.map((r, i) => {
                const style = RISK_STYLES[r.risk_level] || RISK_STYLES.low;
                const Icon = style.icon;
                return (
                  <div key={i} className="border border-border bg-card p-5 flex flex-col gap-3" data-testid={`risk-card-${r.risk_level}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Project</div>
                        <div className="font-heading text-lg mt-0.5 truncate">{r.project_name || "Unnamed"}</div>
                      </div>
                      <div className={`mono text-[9px] uppercase tracking-widest px-2 py-1 ${style.pill}`}>
                        <Icon className="inline h-3 w-3 mr-1" />{r.risk_level} risk
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className={`border rounded p-2 ${style.badge}`}>
                        <div className="mono text-[9px] uppercase tracking-widest">Delay Estimate</div>
                        <div className="font-heading text-2xl mt-0.5 flex items-center gap-1">
                          <Clock className="h-4 w-4" /> {r.delay_days_estimate ?? 0} <span className="text-sm font-normal">days</span>
                        </div>
                      </div>
                      <div className="border border-border rounded p-2">
                        <div className="mono text-[9px] uppercase tracking-widest text-muted-foreground">Confidence</div>
                        <div className="font-heading text-2xl mt-0.5">{r.confidence ?? 0}%</div>
                      </div>
                    </div>

                    {r.primary_reasons?.length > 0 && (
                      <div>
                        <div className="mono text-[9px] uppercase tracking-widest text-muted-foreground mb-1.5">Primary reasons</div>
                        <ul className="text-sm space-y-1">
                          {r.primary_reasons.map((reason, j) => (
                            <li key={j} className="flex gap-2"><span className="text-primary shrink-0">•</span><span>{reason}</span></li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {r.recommended_actions?.length > 0 && (
                      <div className="border-t border-border pt-3">
                        <div className="mono text-[9px] uppercase tracking-widest text-primary mb-1.5">Recommended actions</div>
                        <ul className="text-sm space-y-1">
                          {r.recommended_actions.map((act, j) => (
                            <li key={j} className="flex gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-primary mt-0.5 shrink-0" /><span>{act}</span></li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
            Generated: {new Date(data.generated_at).toLocaleString()}
          </div>
        </>
      )}
    </div>
  );
}

const TONE = {
  muted:   "border-border",
  red:     "border-red-500/40 text-red-500",
  amber:   "border-amber-500/40 text-amber-500",
  emerald: "border-emerald-500/40 text-emerald-500",
};

const SummaryCard = ({ label, val, tone }) => (
  <div className={`border rounded-md p-3 ${TONE[tone] || TONE.muted}`}>
    <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
    <div className="font-heading text-3xl mt-1">{val}</div>
  </div>
);
